import { Box } from '@material-ui/core';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import {
  assignToContactTags,
  fetchContactTags,
  fetchContactTagsById,
  TagsPaginationPayload,
  unassignToContactTags,
} from '@src/reducers/tags';
import {
  getContactTagsById,
  getContactTagsPagination,
  getContactTagsWithAssignation,
} from '@src/selectors/tags';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import AddTagWithScroll from './AddTagWithScroll';
import RowTagsList from './RowTagsList';
import { WIDTH_CHATLIST, WIDTH_CONTEXT_MENU } from './utils';

import './tag-list_style.css';
import { AnyTag } from './types';

const HEIGTH_OF_TAGS_LIST_DIV = 30;
const MIN_PAGE_SIZE_TO_HAVE_SCROLL = 40;
const INIT_PAGE = 1;

interface IContactTagsListView {
  isContextMenuOpened?: boolean;
  contactId?: number;
  chatId: string;
  style?: CSSProperties;
  ellipsis?: boolean;
  columnView?: boolean;
}

const ContactTagsListView: React.FC<IContactTagsListView> = ({
  isContextMenuOpened,
  contactId,
  chatId,
  style = {},
  ellipsis,
  columnView,
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;

  const dispatch = useDispatch();

  const [pagination, setPagination] = useState<TagsPaginationPayload>({
    pageNum: INIT_PAGE,
    pageSize: MIN_PAGE_SIZE_TO_HAVE_SCROLL,
    name: '',
  });
  const [hasNextPage, setHasNextPage] = useState(false);

  const tags = useSelector(getContactTagsById);
  const allContactTags = useSelector(getContactTagsWithAssignation);
  const paginationResponse = useSelector(getContactTagsPagination);

  const handleOnSearchPatternChange = (value: string) => {
    setPagination(prev => ({
      ...prev,
      name: value,
    }));
  };

  const handleOnNextPage = () => {
    setPagination(prev => ({
      ...prev,
      pageSize: prev.pageSize + 10,
    }));
    setHasNextPage(false);
  };

  const updateContactTag = (tag: AnyTag, checked: boolean) => {
    const { tagId } = tag;
    if (checked && contactId && tagId !== undefined) {
      dispatch(
        assignToContactTags({
          contactId,
          tagIds: [tagId],
          chatId,
        })
      );
    } else if (!checked && contactId && tagId !== undefined) {
      dispatch(unassignToContactTags({ contactId, tagId, chatId }));
    }
  };

  useEffect(() => {
    if (contactId) dispatch(fetchContactTagsById({ contactId }));
  }, [contactId, dispatch]);

  useEffect(() => {
    dispatch(fetchContactTags({ type: 'CONTACT', pagination }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination]);

  useEffect(() => {
    setHasNextPage(paginationResponse.hasNextPage);
  }, [paginationResponse]);

  return (
    <Box
      className="ConsoleTagsListView"
      data-testid="contact-tags-panel"
      style={{
        width: `calc(100vw - ${WIDTH_CHATLIST} - ${
          isContextMenuOpened ? WIDTH_CONTEXT_MENU : 0
        }`,
      }}
    >
      <Box
        display="flex"
        flexDirection={columnView ? 'column' : 'row-reverse'}
        justifyContent={columnView ? 'flex-end' : 'space-between'}
        alignItems={columnView ? 'flex-end' : 'center'}
        my={1}
        // @ts-expect-error MUI v4 types incompatible with React 18, will be fixed in MUI v5 migration
        style={style}
      >
        <AddTagWithScroll
          tags={allContactTags}
          label={formatMessage({
            id: 'Tags.console.contacts.addButton.label',
          })}
          title={formatMessage({
            id: 'Tags.console.contacts.addButton.list.title',
          })}
          placeholder={formatMessage({ id: 'TagSelector.Header' })}
          handleUpdateTag={updateContactTag}
          position="left"
          searchPattern={pagination?.name || ''}
          onSearchPatternChange={handleOnSearchPatternChange}
          onNextPage={handleOnNextPage}
          hasNextPage={hasNextPage}
        />
        <Box alignSelf="baseline" mt={0.5}>
          <RowTagsList
            tags={tags}
            heigthOfTagsListDiv={HEIGTH_OF_TAGS_LIST_DIV}
            position="left"
            handleOnDelete={updateContactTag}
            ellipsis={ellipsis}
            columnView={columnView}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ContactTagsListView;
